import { type UseQueryResult, useQuery } from "react-query"
import { useParams, useSearchParams } from "react-router-dom"

import type { AxiosResponse } from "axios"
import LZString from "lz-string"

import { KonanAPI } from "../../../services/KonanAPI"
import type { PaginatedListPredictionsList } from "../../../types/generated/api/PaginatedListPredictionsList"
import type { UseProjectPredictionsOptions } from "../interfaces"

type ParamsType = {
  id: string
}

/**
 * Fetches paginated project predictions.
 */
export function useProjectPredictions({
  enabled,
}: UseProjectPredictionsOptions): UseQueryResult<AxiosResponse<PaginatedListPredictionsList>> {
  const [searchParams] = useSearchParams()
  const { id: projectId } = useParams<ParamsType>()

  // Extract values from query parameters
  const startDate = searchParams.get("startDate")
  const endDate = searchParams.get("endDate")

  const decisionFilters = searchParams.get("decision_filters")
  const filterObject = decisionFilters ? JSON.parse(LZString.decompressFromEncodedURIComponent(decisionFilters)) : {}

  const reviewLabel = searchParams.get("reviewed") as "REVIEWED" | "REVOKED" | undefined
  const jointOutputFilter = searchParams.get("joint_output") || undefined
  const reasonFilter = searchParams.get("reason") || undefined
  const feedbackFilter = searchParams.get("feedback") || undefined

  const page = parseInt(searchParams.get("page") ?? "0") ?? 0
  const pageSize = parseInt(searchParams.get("pageSize") ?? "10") ?? 10
  const pagination = { pageIndex: page, pageSize }

  return useQuery<AxiosResponse<PaginatedListPredictionsList>>(
    [
      "project-predictions",
      startDate,
      endDate,
      pagination,
      filterObject,
      reviewLabel,
      jointOutputFilter,
      reasonFilter,
      feedbackFilter,
    ],
    () =>
      KonanAPI.fetchProjectPredictions(
        startDate as string,
        endDate as string,
        projectId as string,
        pagination.pageIndex + 1,
        pagination.pageSize,
        filterObject,
        reviewLabel,
        jointOutputFilter,
        reasonFilter,
        feedbackFilter,
      ),
    {
      keepPreviousData: true,
      enabled,
    },
  )
}
